import React, { useState } from 'react'
import { connectRefinementList } from 'react-instantsearch-dom'
import PropTypes from 'prop-types'
import { Accordion, Level, Icon, Box, Text, List } from '@deseretbook/react-ui'
import { Analytics } from '../../../../dbcom'
import { ClickableContent } from '../..'
import { trackAlgoliaEvent } from '../analytics'

/**
 * Component for filter items. Should be used to list each item within a refinement list
 *
 * This dynamic component can be used for any attribute that has multiple options. Like categories, or authors.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/refinement-list/react/
 *
 * @param {func} refine - Updates the current value
 * @param {string} label - Filter item label
 * @param {string} value - Value for creating a URL
 * @param {boolean} isRefined - If the current filter is applied
 * @param {number} count - Amount of results containing filter
 *
 * @returns {func} React Component
 */
const FilterItemContainer = ({ refine, label, value, isRefined, count }) => {
  /**
   * Handles/Tracks user applied filter
   */
  const handleClick = () => {
    refine(value)

    Analytics.event(
      {
        category: 'Algolia Filter Item',
        action: `User applied filter, ${label}.`,
      },
      {
        isInteractive: true,
      },
    )
  }

  return (
    <List.Item key={label}>
      <ClickableContent isPaddingless onClick={handleClick}>
        <Level>
          <Level.Left style={{ maxWidth: '80%' }}>
            <Level.Item>
              <Icon name={`checkbox${isRefined ? 'On' : 'Off'}`} />
            </Level.Item>
            <Level.Item style={{ maxWidth: '13rem' }}>
              <Text hasEllipsis title={label}>
                {label}
              </Text>
            </Level.Item>
          </Level.Left>
          <Level.Right>
            <Level.Item>
              <Text>({count})</Text>
            </Level.Item>
          </Level.Right>
        </Level>
      </ClickableContent>
    </List.Item>
  )
}

FilterItemContainer.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  count: PropTypes.number,
  isRefined: PropTypes.bool,
  refine: PropTypes.func,
}

/**
 * Refinement List component for showing categories
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/refinement-list/react/
 *
 * @param {Array} items - List of items to be displayed
 * @param {func} refine - Updates the current value
 * @param {string} label - Refinement list label
 * @param {boolean} isOpenByDefault - Whether to keep the filter open by default
 * @param {string} attribute - Attribute used in algolia
 *
 *@returns {function} React Component
 */
const RefinementListComponent = ({
  items = [],
  refine,
  label,
  isOpenByDefault = false,
  attribute,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault)

  const refinedItems = items.filter((item) => item.isRefined)
  const unrefinedItems = items.filter((item) => !item.isRefined)

  /**
   * Handles/Tracks Accordion toggle event
   */
  const handleToggle = () => {
    Analytics.event(
      {
        category: 'Algolia Filter',
        action: `User ${isOpen ? 'opened' : 'closed'} the ${label} refinement list.`,
      },
      {
        isInteractive: true,
      },
    )

    setIsOpen(!isOpen)
  }

  /**
   * Tracks filter clicks with algolia
   *
   * @param {string} filterValue - The filter value that was clicked
   */
  const trackFilterClick = (filterValue) => {
    trackAlgoliaEvent('clickedFilters', {
      eventName: 'Filter Clicked',
      filters: [`${attribute}:${filterValue}`],
    })
  }

  /**
   * Applies the new value to the current filters and tracks it
   *
   * @param {string} value - The filter value that was clicked
   */
  const handleRefinement = (value) => {
    refine(value)
    trackFilterClick(value)
  }

  return (
    <Accordion.Section isOpen={isOpen} onToggle={handleToggle}>
      <Accordion.Section.Header>
        <Box paddingVertical={0.5}>
          <Level>
            <Level.Left>
              <Level.Item>
                <Text style={{ textTransform: 'uppercase' }}>{label}</Text>
              </Level.Item>
            </Level.Left>
            <Level.Right>
              <Level.Item>
                <Icon name={`chevron${isOpen ? 'Up' : 'Down'}`} />
              </Level.Item>
            </Level.Right>
          </Level>
        </Box>
      </Accordion.Section.Header>
      <Accordion.Section.Content>
        <Box paddingVertical={1} paddingLeft={0.5}>
          <List>
            {refinedItems.map((filterItem) => (
              <FilterItemContainer
                key={filterItem.value}
                refine={handleRefinement}
                {...filterItem}
              />
            ))}
            {unrefinedItems.map((filterItem) => (
              <FilterItemContainer
                key={filterItem.value}
                refine={handleRefinement}
                {...filterItem}
              />
            ))}
          </List>
        </Box>
      </Accordion.Section.Content>
    </Accordion.Section>
  )
}

RefinementListComponent.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      count: PropTypes.number,
      isRefined: PropTypes.bool,
    }),
  ),
  refine: PropTypes.func,
  label: PropTypes.string,
  isOpenByDefault: PropTypes.bool,
  attribute: PropTypes.string,
}

export const RefinementList = connectRefinementList(RefinementListComponent)
